import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaVolumeUp, FaPause } from 'react-icons/fa';
import styles from './ResultsPage.module.css';
import BackButton from '../common/BackButton/BackButton';

const ResultsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Speech synthesis setup
  const synth = window.speechSynthesis;
  
  // Initialize voice when component mounts
  useEffect(() => {
    const initVoice = () => {
      const voices = synth.getVoices();
      // Prioritize British English voices
      const britishVoice = voices.find(voice => voice.lang === 'en-GB');
      const englishVoice = voices.find(voice => voice.lang.startsWith('en-'));
      
      if (britishVoice) {
        setSelectedVoice(britishVoice);
      } else if (englishVoice) {
        setSelectedVoice(englishVoice);
      }
    };

    if (synth.onvoiceschanged !== undefined) {
      synth.onvoiceschanged = initVoice;
    }
    initVoice();
    
    return () => {
      // Cancel any ongoing speech when component unmounts
      synth.cancel();
    };
  }, []);
  
  useEffect(() => {
    // Get session results from location state
    if (location.state?.sessionResults) {
      setResults(location.state.sessionResults);
      setQuestions(location.state.questions || []);
      setLoading(false);
    } else {
      setError("No session results found. Please try again.");
      setLoading(false);
    }
  }, [location]);

  const handleReturnHome = () => {
    navigate('/');
  };
  
  const playWord = (word) => {
    // Cancel any ongoing speech
    synth.cancel();
    
    if (playingAudio === word) {
      setPlayingAudio(null);
      return;
    }
    
    const utterance = new SpeechSynthesisUtterance(word);
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
    utterance.rate = 0.6; // Slower for better clarity
    utterance.pitch = 1;
    
    utterance.onend = () => {
      setPlayingAudio(null);
    };
    
    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setPlayingAudio(null);
    };
    
    setPlayingAudio(word);
    synth.speak(utterance);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
        <span>{error}</span>
      </div>
    );
  }

  if (!results || !results.stats) {
    return (
      <div className={styles.error}>
        <span>No results data available</span>
      </div>
    );
  }

  const stats = results.stats;
  const correctCount = stats.correctAnswers || 0;
  const totalCount = results.questions?.length || 0;
  const accuracy = stats.accuracy || 0;
  
  // Get incorrect questions for review
  const incorrectQuestions = results.questions?.filter(q => !q.isCorrect) || [];

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h2 className={styles.title}>Quiz Results</h2>
          <div className={styles.modeIndicator}>
            {results.mode?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Mode
          </div>
        </div>
        
        <div className={styles.statsContainer}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{correctCount}/{totalCount}</div>
            <div className={styles.statLabel}>Questions Correct</div>
          </div>
          
          <div className={styles.statCard}>
            <div className={styles.statValue}>{Math.round(accuracy)}%</div>
            <div className={styles.statLabel}>Accuracy</div>
          </div>
          
          <div className={styles.statCard}>
            <div className={styles.statValue}>{Math.round(stats.totalTime)}s</div>
            <div className={styles.statLabel}>Total Time</div>
          </div>
          
          {/* {stats.timedStats?.wpm && (
            <div className={styles.statCard}>
              <div className={styles.statValue}>{stats.timedStats.wpm}</div>
              <div className={styles.statLabel}>Words Per Minute</div>
            </div>
          )} */}
        </div>
        
        {results.feedback && (
          <div className={styles.feedbackSection}>
            <h3>Feedback</h3>
            
            {results.feedback.strengths && results.feedback.strengths.length > 0 && (
              <div className={styles.feedbackItem}>
                <h4>Strengths</h4>
                <ul>
                  {results.feedback.strengths.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {results.feedback.weaknesses && results.feedback.weaknesses.length > 0 && (
              <div className={styles.feedbackItem}>
                <h4>Areas for Improvement</h4>
                <ul>
                  {results.feedback.weaknesses.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {results.feedback.recommendations && results.feedback.recommendations.length > 0 && (
              <div className={styles.feedbackItem}>
                <h4>Recommendations</h4>
                <ul>
                  {results.feedback.recommendations.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {results.feedback && results.feedback.recommendedLearningPaths && Object.keys(results.feedback.recommendedLearningPaths).length > 0 && (
              <div className={styles.learningPathsSection}>
                <h4>Recommended Learning Paths</h4>
                <div className={styles.learningPathsContainer}>
                  {Object.entries(results.feedback.recommendedLearningPaths).map(([path, reason], index) => {
                    // Map learning path to app name and determine if it's available
                    const appMap = {
                      'grammar': { name: 'Grammarly', icon: '📝', available: true, appValue: 'grammar' },
                      'writing': { name: 'Writely', icon: '✍️', available: true, appValue: 'writing' },
                      'spelling': { name: 'Spelling Pro', icon: '📚', available: true, appValue: 'spelling' },
                      'vocabulary': { name: 'Vocab Master', icon: '🔤', available: false },
                      'reading': { name: 'Readly', icon: '📖', available: true, url: 'https://readly.scholarlytraining.com/' },
                      'speaking': { name: 'Speakify', icon: '🗣️', available: true, url: 'https://speakify.scholarlytraining.com/' }
                    };
                    
                    const app = appMap[path] || { 
                      name: path.charAt(0).toUpperCase() + path.slice(1), 
                      icon: '📋', 
                      available: false 
                    };
                    
                    return (
                      <div key={index} className={styles.learningPathCard}>
                        <div className={styles.learningPathHeader}>
                          <span className={styles.learningPathIcon}>{app.icon}</span>
                          <div className={styles.learningPathName}>{app.name}</div>
                        </div>
                        <div className={styles.learningPathReason}>{reason}</div>
                        {app.available ? (
                          <button 
                            className={styles.learningPathButton}
                            onClick={() => {
                              if (app.url) {
                                window.location.href = app.url;
                              } else {
                                localStorage.setItem('selectedApp', app.appValue);
                                window.location.href = '/';
                              }
                            }}
                          >
                            Start Learning
                          </button>
                        ) : (
                          <button 
                            className={`${styles.learningPathButton} ${styles.disabledButton}`}
                            disabled
                          >
                            Coming Soon
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        
        {incorrectQuestions.length > 0 && (
          <div className={styles.incorrectQuestionsSection}>
            <h3>Questions to Review</h3>
            
            {incorrectQuestions.map((questionAttempt, index) => {
              const question = questions.find(q => q._id === questionAttempt.questionId);
              if (!question) return null;
              
              // Determine if this is a spelling question
              const isSpellingQuestion = question.type && [
                'word_completion', 'audio_spelling', 'anagram_challenge', 
                'definition_spelling', 'spelling_bee', 'story_spelling'
              ].includes(question.type);
              
              // Check if this is an audio spelling question
              const isAudioQuestion = question.type === 'audio_spelling';
              
              return (
                <div key={index} className={styles.reviewQuestion}>
                  {/* Display appropriate question text based on question type */}
                  <div className={styles.reviewQuestionText}>
                    {isSpellingQuestion ? (
                      <div>
                        {question.type === 'word_completion' && (
                          <div> {question.content.text} </div>
                        )}
                        {question.type === 'audio_spelling' && (
                          <div className={styles.audioQuestionReview}>
                            <span>Spell the word you heard:</span>
                            <button 
                              onClick={() => playWord(question.content.correctAnswer)}
                              className={styles.audioButton}
                            >
                              {playingAudio === question.content.correctAnswer ? <FaPause /> : <FaVolumeUp />}
                              {playingAudio === question.content.correctAnswer ? 'Stop' : 'Play Word'}
                            </button>
                          </div>
                        )}
                        {question.type === 'anagram_challenge' && (
                          <div>Unscramble the letters: <strong>{question.content.scrambledVersion}</strong></div>
                        )}
                        {question.type === 'definition_spelling' && (
                          <div>Spell the word: <strong>{question.content.definition}</strong></div>
                        )}
                        {!['word_completion', 'audio_spelling', 'anagram_challenge', 'definition_spelling'].includes(question.type) && (
                          <div>{question.content.text}</div>
                        )}
                      </div>
                    ) : (
                      <div>{question.content.text}</div>
                    )}
                  </div>
                  
                  <div className={styles.reviewAnswers}>
                    <div className={styles.incorrectAnswer}>
                      Your answer: {questionAttempt.userAnswer || 'No answer provided'}
                    </div>
                    <div className={styles.correctAnswer}>
                      Correct answer: {question.content.correctAnswer}
                      {isAudioQuestion && (
                        <button 
                          onClick={() => playWord(question.content.correctAnswer)}
                          className={styles.inlineAudioButton}
                        >
                          <FaVolumeUp />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Show personalized explanation if available */}
                  {questionAttempt.personalizedExplanation ? (
                    <div className={styles.personalizedExplanation}>
                      <h5>Why your answer was incorrect:</h5>
                      <p>{questionAttempt.personalizedExplanation}</p>
                    </div>
                  ) : (
                    <div className={styles.reviewExplanation}>{question.content.explanation}</div>
                  )}
                  
                  {/* For spelling questions, show additional helpful information */}
                  {isSpellingQuestion && question.content.hints && question.content.hints.length > 0 && (
                    <div className={styles.spellingHints}>
                      <h5>Hints:</h5>
                      <ul>
                        {question.content.hints.map((hint, i) => (
                          <li key={i}>{hint}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {isSpellingQuestion && question.content.usageExample && (
                    <div className={styles.spellingExample}>
                      <h5>Example:</h5>
                      <p>{question.content.usageExample}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        <button 
          className={styles.finishButton}
          onClick={handleReturnHome}
        >
          Return to Home
        </button>
        
        <BackButton />
      </div>
    </div>
  );
};

export default ResultsPage; 